/*
 * Copyright (c) 2026 Talent Catalog.
 *
 * This program is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or any later version.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License
 * for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see https://www.gnu.org/licenses/.
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import {Candidate, CandidateStatus, Gender, UnhcrStatus} from '../../../model/candidate';
import {Country} from '../../../model/country';
import {CountryService} from '../../../services/country.service';
import {Language} from '../../../model/language';
import {LanguageService} from '../../../services/language.service';
import {SearchResults} from '../../../model/search-results';

import {NgbDate, NgbDateStruct, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup
} from '@angular/forms';
import {SearchSavedSearchesComponent} from '../load-search/search-saved-searches.component';
import {CreateUpdateSearchComponent} from '../create-update/create-update-search.component';
import {SavedSearchService} from '../../../services/saved-search.service';
import {forkJoin, Subject} from 'rxjs';
import {JoinSavedSearchComponent} from '../join-search/join-saved-search.component';
import {EducationLevel} from '../../../model/education-level';
import {EducationLevelService} from '../../../services/education-level.service';
import {EducationMajor} from '../../../model/education-major';
import {EducationMajorService} from '../../../services/education-major.service';
import {EmbeddingModel} from '../../../model/embedding-model';
import {Occupation} from '../../../model/occupation';
import {CandidateOccupationService} from '../../../services/candidate-occupation.service';
import {
  emptyLanguageLevelFormControlModel,
  LanguageLevelFormControlModel
} from '../../util/form/language-proficiency/language-level-form-control-model';
import * as moment from 'moment-timezone';
import {LanguageLevel} from '../../../model/language-level';
import {LanguageLevelService} from '../../../services/language-level.service';
import {
  DateRangePickerComponent
} from '../../util/form/date-range-picker/date-range-picker.component';
import {
  LanguageLevelFormControlComponent
} from '../../util/form/language-proficiency/language-level-form-control.component';
import {ActivatedRoute, Router} from '@angular/router';
import {
  ClearSelectionRequest,
  getCandidateSourceNavigation,
  getSavedSearchBreadcrumb,
  SavedSearch,
  SearchCandidateRequestPaged
} from '../../../model/saved-search';
import {CandidateSource, CandidateSourceType} from '../../../model/base';
import {ConfirmationComponent} from '../../util/confirm/confirmation.component';
import {User} from '../../../model/user';
import {AuthorizationService} from '../../../services/authorization.service';
import {enumKeysToEnumOptions, EnumOption, enumOptions, isEnumOption} from "../../../util/enum";
import {SearchCandidateRequest} from "../../../model/search-candidate-request";
import {SurveyTypeService} from "../../../services/survey-type.service";
import {SurveyType} from "../../../model/survey-type";
import {Partner} from "../../../model/partner";
import {PartnerService} from "../../../services/partner.service";
import {AuthenticationService} from "../../../services/authentication.service";
import {SearchQueryService} from "../../../services/search-query.service";
import {debounceTime, first, takeUntil} from "rxjs/operators";
import {JobService} from "../../../services/job.service";
import {hasTextContent} from "../../../util/string";
import {SkillName} from "../../../model/skill";
import {CandidateNumberParser} from "../../../util/candidate-number-parser";
import {EmbeddingModelService} from "../../../services/embedding-model.service";
import {JobMatchingInfo} from "../../../model/JobMatchingInfo";
import {ExtractSkillsRequest, SkillsService} from "../../../services/skills.service";

/**
 * This component contains all the search fields for saved and unsaved searches. It communicates
 * with the parent component candidates-search which contains the results part of the search.
 * Communicating with the parent allows us trigger the unsaved-changes router guard used on saved searches.
 * Some of the search fields are simple form controls, others are child components which pass values
 * to the form control. When patchValue is called it doesn't mark the form as dirty,
 * so this must be done manually. The unsaved changes router guard requires the dirty state, so this
 * needs to be emitted to the parent component. We automate emit of the dirty status if the
 * form value changes(via patchValue or user form changes). However, if we are updating the form values in
 * a different way (eg. add/delete base search) we need to make sure to emit the dirty status.
 * After a saved search is initially loaded into the form controls, we manually set the form status
 * to pristine. This will override any places the form controls have been marked as dirty when
 * populating as we don't want to trigger the unsaved changes guard after a search load, and we want
 * the search button disabled as no user changes have been made to the form.
 */
@Component({
  selector: 'app-define-search',
  templateUrl: './define-search.component.html',
  styleUrls: ['./define-search.component.scss']
})
export class DefineSearchComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('modifiedDate', {static: true}) modifiedDatePicker: DateRangePickerComponent;
  @ViewChild('englishLanguage', {static: true}) englishLanguagePicker: LanguageLevelFormControlComponent;
  @ViewChild('otherLanguage', {static: true}) otherLanguagePicker: LanguageLevelFormControlComponent;
  @ViewChild('formWrapper', {static: true}) formWrapper: ElementRef;
  @ViewChild('downloadCsvErrorModal', {static: true}) downloadCsvErrorModal;

  @Input() jobId: number;
  jobName: string;  //Populated when JobMatchingInfo is fetched.
  /**
   * ID of the job that jobName belongs to - either the job passed in via the jobId input (job
   * query param), or the job associated with the loaded saved search.
   */
  jobNameSourceId: number;

  @Input() listId: number;
  @Input() savedSearch: SavedSearch;
  @Input() pageNumber: number;
  @Input() pageSize: number;

  @Output() onFormChange = new EventEmitter<boolean>();

  error: any;
  loading: boolean;
  loadingJobMatchingInfo: boolean;
  searchForm: UntypedFormGroup;
  showSearchRequest: boolean = false;
  results: SearchResults<Candidate>;
  savedSearchId;

  searchRequest: SearchCandidateRequestPaged;

  /* DATA - these are all drop down options for each select field*/
  embeddingModels: EmbeddingModel[];
  nationalities: Country[];
  countries: Country[];
  partners: Partner[];
  languages: Language[];
  educationLevels: EducationLevel[];
  educationMajors: EducationMajor[];
  candidateOccupations: Occupation[];
  languageLevels: LanguageLevel[];
  surveyTypes: SurveyType[];

  notElastic;

  candidateStatusOptions: EnumOption[] = enumOptions(CandidateStatus);
  currentSearchTerms: string[] = [];
  genderOptions: EnumOption[] = enumOptions(Gender);
  selectedCandidate: Candidate;
  selectedCandidates: Candidate[];
  englishLanguageModel: LanguageLevelFormControlModel;
  otherLanguageModel: LanguageLevelFormControlModel;
  loggedInUser: User;
  unhcrStatusOptions: EnumOption[] = enumOptions(UnhcrStatus);

  //Used to store (and display) skills extracted from a job description (when jobName is specified).
  extractedSkills: string;
  extractedSkillNames: string[] = [];

  selectedBaseJoin;
  storedBaseJoin;
  /**
   * Will be true whenever there is any text in the Keyword Search input - can be used to hide filter
   * that doesn't have ES capability, though the practice should generally be avoided.
   */
  searchIsElastic: boolean = false;

  //Used to unsubscribe from long-lived subscriptions (form/service listeners) on destroy.
  private destroy$ = new Subject<void>();

  constructor(private fb: UntypedFormBuilder,
              private countryService: CountryService,
              private embeddingModelService: EmbeddingModelService,
              private languageService: LanguageService,
              private partnerService: PartnerService,
              private savedSearchService: SavedSearchService,
              private educationLevelService: EducationLevelService,
              private educationMajorService: EducationMajorService,
              private candidateOccupationService: CandidateOccupationService,
              private surveyTypeService: SurveyTypeService,
              private jobService: JobService,
              private languageLevelService: LanguageLevelService,
              private modalService: NgbModal,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private skillsService: SkillsService,
              private authorizationService: AuthorizationService,
              private authenticationService: AuthenticationService,
              private searchQueryService: SearchQueryService
              ) {
    /* SET UP FORM */
    //todo For fixing this deprecation see https://stackoverflow.com/questions/65155217/formbuilder-group-is-deprecated
    this.searchForm = this.fb.group({
      requirements: [null],
      lexicalWeight: [0.5],
      modelKey: [null],
      nMatches: [null],
      savedSearchId: [null],
      simpleQueryString: [null],
      candidateNumbers: [''],
      keyword: [null],
      statuses: [[]],
      gender: [null],
      occupationIds: [[]],
      minYrs: [null],
      maxYrs: [null],
      partnerIds: [[]],
      nationalityIds: [[]],
      nationalitySearchType: ['or'],
      countryIds: [[]],
      countrySearchType: ['or'],
      englishMinWrittenLevel: [null],
      englishMinSpokenLevel: [null],
      otherLanguageId: [null],
      otherMinWrittenLevel: [null],
      otherMinSpokenLevel: [null],
      lastModifiedFrom: [null],
      lastModifiedTo: [null],
      createdFrom: [null],
      createdTo: [null],
      timezone: moment.tz.guess(),
      minAge: [null],
      maxAge: [null],
      minEducationLevel: [],
      maxEducationLevel: [],
      educationMajorIds: [[]],
      surveyTypeIds: [[]],
      miniIntakeCompleted: [null],
      fullIntakeCompleted: [null],
      searchJoinRequests: this.fb.array([]),
      unhcrStatuses: [[]],
      //for display purposes
      occupations: [[]],
      countries: [[]],
      partners: [[]],
      educationMajors: [[]],
      nationalities: [[]],
      regoReferrerParam: [null],
      regoUtmCampaign: [null],
      regoUtmSource: [null],
      regoUtmMedium: [null],
      statusesDisplay: [[]],
      surveyTypes: [[]],
      exclusionListId: [null],
      listAnyIds: [[]],
      listAnySearchType: [null],
      listAllIds: [[]],
      listAllSearchType: [null],
      unhcrStatusesDisplay: [[]],
      includePendingTermsCandidates: [false],
      potentialDuplicate: [null]
    }, {validator: this.validateDuplicateSearches('savedSearchId')});

    // Subscribe to changes in Keyword Search
    this.searchForm.controls.simpleQueryString.statusChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.searchIsElastic = this.searchForm.controls.simpleQueryString.dirty &&
        this.searchForm.controls.simpleQueryString.value !== '';
    });

    //Map changes to nMatches to this.pageSize.
    this.searchForm.controls.nMatches.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      //If nMatches is undefined, don't change pageSize
      this.pageSize = this.searchForm.controls.nMatches.value || this.pageSize;
    })

    //Extract skills when requirements change after a short delay.
    this.searchForm.controls.requirements.valueChanges.pipe(
      debounceTime(3000),
      takeUntil(this.destroy$)
    ).subscribe(
      () => this.extractSkills()
    )
  }

  ngOnInit() {
    this.selectedCandidate = null;
    this.loggedInUser = this.authenticationService.getLoggedInUser();
    this.storedBaseJoin = null;
    this.notElastic = {
      readonly: this.elastic()
    }
    this.loading = true;
    this.error = null;

    this.searchQueryService.currentSearchTerms$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(searchTerms => {
      this.currentSearchTerms = searchTerms;
    })

    this.searchForm.get('simpleQueryString').valueChanges.pipe(
        first()
    ).subscribe((simpleQueryString) => {
      this.updateTextSearchQuery(simpleQueryString, this.extractedSkills);
    });

    forkJoin({
      'embeddingModels': this.embeddingModelService.loadReadyModels(),
      'nationalities': this.countryService.listCountries(),
      'countriesRestricted': this.countryService.listCountriesRestricted(),
      'languages': this.languageService.listLanguages(),
      'languageLevels': this.languageLevelService.listLanguageLevels(),
      'educationLevels': this.educationLevelService.listEducationLevels(),
      'majors': this.educationMajorService.listMajors(),
      'partners': this.partnerService.listSourcePartners(),
      'occupations': this.candidateOccupationService.listOccupations(),
      'surveyTypes': this.surveyTypeService.listSurveyTypes()
    }).subscribe(results => {
      this.loading = false;
      this.embeddingModels = results['embeddingModels'];
      this.nationalities = results['nationalities'];
      this.countries = results['countriesRestricted'];
      this.languages = results['languages'];
      this.partners = results['partners'];
      this.languageLevels = results['languageLevels'];
      this.educationLevels = results['educationLevels'];
      this.educationMajors = results['majors'];
      this.candidateOccupations = results['occupations'];
      this.surveyTypes = results['surveyTypes'];

      const englishLanguageObj = this.languages.find(l => l.name.toLowerCase() === 'english');
      this.englishLanguageModel = Object.assign(emptyLanguageLevelFormControlModel, {languageId: englishLanguageObj.id || null});

      this.otherLanguageModel = Object.assign(emptyLanguageLevelFormControlModel)

      if (this.savedSearch) {
        this.loadSavedSearch(this.savedSearch.id);

        //By default, hide the saved search's request details unless we are
        //showing the default search (ie an unsaved search).
        this.showSearchRequest = this.savedSearch.defaultSearch;
      }
    }, error => {
      this.loading = false;
      this.error = error;
    });
    // Listen to form changes and emit form dirty status to candidate search component.
    // The unsaved changes guard is implemented on the saved search route, see app-routing.module.ts.
    // This guard will throw confirmation modal if navigating away with unsaved search fields, which
    // is determined if the form is dirty or not.
    this.searchForm.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.onFormChange.emit(this.searchForm.dirty);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get lexicalWeight(): number {
    return this.searchForm.controls.lexicalWeight.value;
  }

  get embeddingModel(): EmbeddingModel {
    //Look up model from selected modelKey
    let embeddingModel: EmbeddingModel | null;
    const modelKey = this.searchForm.controls.modelKey.value;
    if (modelKey) {
      embeddingModel = this.embeddingModels.find(model => model.modelKey === modelKey);
    } else {
      embeddingModel = null;
    }

    return embeddingModel;
  }

  public hasEmbeddingModel(): boolean {
    return !!this.embeddingModel;
  }

  public hasEmbeddingModels(): boolean {
    return !!this.embeddingModels && this.embeddingModels.length > 0;
  }

  get requirements(): string {
    return this.searchForm.controls.requirements.value;
  }

  public hasRequirements(): boolean {
    return hasTextContent(this.requirements);
  }

  displayJobNameAsSource(): string {
    return this.jobName ? `(Autopopulated from job ${this.jobNameSourceId}: ${this.jobName})` : '';
  }

  private runSearchWithListConstraint(listId: number) {
    this.clearForm();
    this.initializeListConstraint(listId);
    this.onSubmit();
  }

  private initializeListConstraint(listId: number) {
      this.searchForm.controls.listAnyIds.patchValue([listId]);
  }

  /**
   * Clears the form and runs a search matching the job.
   * <p/>
   * The job's own text only initialises the job's matching requirements. Once the job has
   * requirements (possibly refined by users), they take precedence - so that simply opening a
   * search for the job doesn't overwrite them.
   * @param jobMatchingInfo Matching info for the job. Its description is the job's current
   * matching requirements, if any, otherwise the job's own text.
   * @param jobRequirements Requirements already associated with the job by the search being
   * opened, if any.
   */
  private setUpJobMatch(jobMatchingInfo: JobMatchingInfo, jobRequirements?: string | null) {
    this.clearForm();
    this.jobName = jobMatchingInfo.jobName;
    this.jobNameSourceId = this.jobId;
    this.initializeRequirementsWithDescription(
      hasTextContent(jobRequirements) ? jobRequirements : jobMatchingInfo.description);
    this.setExtractedSkills(jobMatchingInfo.skillNames);
    this.onSubmit();
  }

  private initializeRequirementsWithDescription(description: string) {
    this.searchForm.controls.requirements.patchValue(description);
    this.searchForm.markAsDirty();
  }

  private setExtractedSkills(skills: SkillName[]) {
    if (skills && skills.length > 0) {
      //Construct query string as skill names separated by spaces.
      //If a skill name is multiple words, then surround it with double quotes.
      let queryString = skills
      .map(
        s => s.name.indexOf(' ') < 0 ? s.name : '"' + s.name + '"'
      ).join(' ');
      this.extractedSkills = queryString;
      this.extractedSkillNames = skills.map(s => s.name);
    } else {
      this.extractedSkills = "";
      this.extractedSkillNames = [];
    }

    //Update text search query to update highlighting
    this.updateTextSearchQuery(this.searchForm.value.simpleQueryString, this.extractedSkills);
  }

  // Stops Keyword Search tooltip from opening on keydown.enter in inputs
  ngAfterViewInit() {
    const inputs: NodeList = document.querySelectorAll('input')
    inputs.forEach(input => {
      input.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      })
    })
  }

  get simpleQueryString(): string {
    return this.searchForm.value.simpleQueryString;
  }

  elastic(): boolean {
    return this.simpleQueryString != null && this.simpleQueryString.length > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.savedSearch) {
      if (changes.savedSearch.previousValue !== changes.savedSearch.currentValue) {
        if (this.savedSearch) {
          this.savedSearchId = this.savedSearch.id;

          //The very first change will be loaded after ngOnInit finishes.
          if (!changes.savedSearch.isFirstChange()) {
            this.loadSavedSearch(this.savedSearchId);
          }
        }
      }
    }
  }

  validateDuplicateSearches(id: string) {
    return (group: UntypedFormGroup): { [key: string]: any } => {
      const savedSearchId = group.controls[id].value;
      if (this.selectedBaseJoin){
        const baseJoinId = this.selectedBaseJoin.savedSearchId;
        if (savedSearchId && baseJoinId && savedSearchId === baseJoinId) {
          return {
            error: "Can't select same base search as saved search."
          };
        }
        return {};
      }
    }
  }

  getError(){
    if (this.error) {
      return this.error;
    } else if (this.searchForm.hasError('error')){
      return this.searchForm.getError('error');
    } else {
      return null;
    }
  }

  onSubmit() {
   //checkSelectionsAndApply
   // If there are candidates selected, run a check before applying search.
    if (this.selectedCandidates?.length > 0) {
      this.confirmClearSelectionAndApply();
    } else {
      this.apply();
    }
  }

  runKeywordSearchOnEnter(event: KeyboardEvent) {
    event.preventDefault(); // Stop unintended effect (Keyword Search tooltip opening)
    this.apply();
  }
  
  apply() {
    //Initialize a search request from the modified formData
    const request: SearchCandidateRequestPaged =
      this.getIdsMultiSelect(this.searchForm.value)

    //Note that just changing searchRequest triggers the display of the results
    //See the html of this component, for which app-show-candidates takes
    //searchRequest as an input.
    //The receiving code automatically resets the page number to 0 for changed search requests.
    //See ngOnChanges of ShowCandidatesComponent.
    this.searchRequest = request;

    this.updateTextSearchQuery(this.searchForm.value.simpleQueryString, this.extractedSkills);

    // Search automatically saves the filters when enabled.
    if (this.savedSearch?.autoUpdateOnSearch !== false) {
      this.searchForm.markAsPristine();
      this.onFormChange.emit(false);
    }
  }
  /**
   * Updates the search query service with the combined text search query, which includes the
   * simpleQueryString and any extracted skills.
   * @param simpleQueryString The current simple query string text.
   * @param extractedSkills Any skills extracted from the requirements text, or empty if none.
   */
  updateTextSearchQuery(simpleQueryString: string, extractedSkills: string): void {
    let combinedTextSearchQuery: string = simpleQueryString ?? '';
    if (extractedSkills) {
      // If there are extracted skills, we want to append them to the simpleQueryString
      combinedTextSearchQuery += (combinedTextSearchQuery ? ' ' : '') + extractedSkills;
    }
    this.searchQueryService.changeSearchQuery(combinedTextSearchQuery);
  }

  /**
   * Replaces arrays of objects with their corresponding ids.
   * @param request Form data
   */
  getIdsMultiSelect(request): SearchCandidateRequestPaged {
    request.candidateNumbers = CandidateNumberParser.parseCandidateNumbers(request.candidateNumbers);

    if (request.countries != null) {
      request.countryIds = request.countries.map(c => c.id);
      delete request.countries;
    }

    if (request.partners != null) {
      request.partnerIds = request.partners.map(n => n.id);
      delete request.partners;
    }

    if (request.nationalities != null) {
      request.nationalityIds = request.nationalities.map(n => n.id);
      delete request.nationalities;
    }

    if (request.occupations != null) {
      request.occupationIds = request.occupations.map(o => o.id);
      delete request.occupations;
    }

    if (request.educationMajors != null) {
      request.educationMajorIds = request.educationMajors.map(o => o.id);
      delete request.educationMajors;
    }

    if (request.statusesDisplay != null) {
      //Pick up key values from EnumOptions
      request.statuses = request.statusesDisplay.map(s => s.key);
      delete request.statusesDisplay;
    }

    if (request.surveyTypes != null) {
      request.surveyTypeIds = request.surveyTypes.map(s => s.id);
      delete request.surveyTypes;
    }

    if (request.unhcrStatusesDisplay != null) {
      //Pick up key values from EnumOptions
      request.unhcrStatuses = request.unhcrStatusesDisplay.map(s => s.key);
      delete request.unhcrStatusesDisplay;
    }

    return request;
  }

  clearForm() {
    this.searchForm.reset();
    // We need to add back the saved search id because we are always working with a saved search object,
    // either it's a default saved search or saved search.
    this.searchForm.controls['savedSearchId'].patchValue(this.savedSearchId);
    this.searchForm.controls['candidateNumbers'].patchValue('');
    this.searchForm.controls['countrySearchType'].patchValue('or');
    this.searchForm.controls['nationalitySearchType'].patchValue('or');
    this.searchForm.controls['listAllSearchType'].patchValue(null);
    this.searchForm.controls['listAnySearchType'].patchValue(null);

    while (this.searchJoinArray.length) {
      this.searchJoinArray.removeAt(0); // Clear the form array
    }
    this.selectedBaseJoin = null;
    this.storedBaseJoin = null;

    this.modifiedDatePicker.clearDates();
    this.englishLanguagePicker.clearProficiencies();
    this.otherLanguagePicker.form.reset();
    this.searchForm.markAsDirty();
  }

  /**
   * Handles the user clicking the "Clear Search" button: clears the search form (as clearForm()
   * always has) and, in addition, explicitly clears any job association on the default search.
   * <p/>
   * This is deliberately NOT folded into clearForm() itself, since clearForm() is also called
   * internally by setUpJobMatch()/runSearchWithListConstraint() as a generic "reset form fields"
   * step before auto-populating a job- or list-driven search - contexts where clearing the job
   * association would immediately undo the job this method is in the middle of setting up.
   */
  onClearSearch() {
    this.clearForm();
    this.clearJobAssociation();
  }

  /**
   * Explicitly clears any job association on the user's default saved search (via the dedicated
   * job-association endpoint - see TC-1535) and removes the 'job' query param from the URL, so
   * that reinitializing from the same URL doesn't immediately reassign the job just cleared.
   * <p/>
   * Only applicable to the default search - a named saved search's persisted job association is
   * only ever changed by an explicit save, not by clearing the search form.
   */
  private clearJobAssociation() {
    if (!this.savedSearch?.defaultSearch) {
      return;
    }

    this.clearJobNameDisplay();

    if (this.savedSearch.sfJobOpp != null) {
      this.savedSearchService.updateJob(this.savedSearch.id, null).subscribe({
        next: updated => {
          this.savedSearch = updated;
        },
        error: err => {
          this.error = err;
        }
      });
    }

    if (this.jobId) {
      this.jobId = 0;
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {job: null},
        queryParamsHandling: 'merge'
      });
    }
  }

  newSearch() {
    this.router.navigate(['search']);
  }

  confirmClearSelectionAndApply() {
    const clearSelectionModal = this.modalService.open(ConfirmationComponent, {
      centered: true,
      backdrop: 'static'
    });

    clearSelectionModal.componentInstance.title = "Your selections will be cleared";
    clearSelectionModal.componentInstance.message =
      "Changing the search filters will clear any candidates selected. " +
      "<br><br>If you would like to keep your selections please save selections to a list before searching. " +
      "Or to proceed without saving selections just click OK.";

    return clearSelectionModal.result
      .then((confirmation) => {
        if (confirmation == true) {
          this.clearSelection();
        }
      })
      .catch(() => { /* Isn't possible */
      });
  }

  clearSelection() {
    const request: ClearSelectionRequest = {
      userId: this.loggedInUser.id,
    };
    this.savedSearchService.clearSelection(this.savedSearch.id, request).subscribe(
      () => {
        this.selectedCandidates = [];
        this.apply();
      },
      err => {
        this.error = err;
      });
  }

  getBreadcrumb() {
    const infos = this.savedSearchService.getSavedSearchTypeInfos();
    return getSavedSearchBreadcrumb(this.savedSearch, infos);
  }

  loadSavedSearch(id) {
    this.loading = true;
    this.searchForm.controls['savedSearchId'].patchValue(id);

    // Clear the search join array and remove base search
    if (this.searchJoinArray.length) {
      this.searchJoinArray.removeAt(0);
    }
    this.selectedBaseJoin = null;

    this.savedSearchService.load(id).subscribe(
      (request) => {
        this.populateFormWithSavedSearch(request);

        //Display (only) the job this saved search is associated with, if any - unless a job
        //passed in explicitly (jobId) is about to set up a job match below, which takes precedence.
        this.clearJobNameDisplay();
        if (!this.jobId) {
          this.displayAssociatedJob(this.savedSearch?.sfJobOpp?.id);
        }

        //If this is a new search generated from a job or a list, clear any existing
        //search params and automatically run a search configured accordingly.
        //We don't want to keep any previous search details from earlier searches.
        if (this.jobId || this.listId) {

          if (this.jobId) {
            //If this search is already associated with the job, its requirements are the
            //job's shared matching requirements.
            const jobRequirements =
              this.savedSearch?.sfJobOpp?.id === this.jobId ? request.requirements : null;
            this.loadingJobMatchingInfo = true;
            //Load the job-matching info
            this.jobService.getJobMatchingInfo(this.jobId).subscribe({
                next: (jobMatchingInfo) => {
                  this.setUpJobMatch(jobMatchingInfo, jobRequirements);
                  this.loadingJobMatchingInfo = false;
                },
                error: (error) => {
                  this.error = error;
                  this.loadingJobMatchingInfo = false;
                }
              })
          } else if (this.listId) {
            //Load the list id into one of the list search fields.
            this.runSearchWithListConstraint(this.listId);
          }
        }

        this.loading = false;
      },
      error => {
        this.error = error;
        this.loading = false;
      });
  }

  /**
   * Displays the name of the given job (the job associated with the loaded saved search) in the
   * same way as a job passed in via the job query param - but purely as a label: unlike
   * setUpJobMatch, it doesn't replace the saved search's own criteria with the job's or re-run
   * the search.
   * <p/>
   * Only the job name is used from the response. Failure just means no label is shown - it
   * doesn't affect the search itself, so it isn't surfaced as an error.
   */
  private displayAssociatedJob(jobId: number | undefined) {
    if (!jobId) {
      return;
    }
    this.jobService.getJobMatchingInfo(jobId).subscribe({
      next: jobMatchingInfo => {
        //Ignore a stale response if a different saved search/job has since been loaded.
        if (!this.jobId && this.savedSearch?.sfJobOpp?.id === jobId) {
          this.jobName = jobMatchingInfo.jobName;
          this.jobNameSourceId = jobId;
        }
      },
      error: () => { /* No label - see above */ }
    });
  }

  private clearJobNameDisplay() {
    this.jobName = null;
    this.jobNameSourceId = null;
  }

  onExclusionListSelected(list: CandidateSource) {
    this.exclusionListIdControl.markAsDirty();
    this.exclusionListIdControl.patchValue(list?.id);
  }

  onListAnySelected(lists: CandidateSource[]) {
    //Update form value
    let ids: number[] = lists.map(s => s.id);
    this.listAnyIdsControl.markAsDirty();
    this.listAnyIdsControl.patchValue(ids);
  }

  onListAllSelected(lists: CandidateSource[]) {
    //Update form value
    let ids: number[] = lists.map(s => s.id);
    this.listAllIdsControl.markAsDirty();
    this.listAllIdsControl.patchValue(ids);
  }

  showSavedSearches() {
    const showSavedSearchesModal = this.modalService.open(SearchSavedSearchesComponent, {
      centered: true,
      backdrop: 'static'
    });

    showSavedSearchesModal.result
      .then((savedSearch) => {
        this.savedSearch = savedSearch;
        this.loadSavedSearch(savedSearch.id);
      })
      .catch(() => { /* Isn't possible */
      });
  }

  createNewSavedSearchModal() {
    this.openSavedSearchModal(true);
  }

  // Update coming from this define search component
  updateSavedSearchModal() {
    this.openSavedSearchModal(false);
  }

  openSavedSearchModal(create: boolean) {
    const showSaveModal = this.modalService.open(CreateUpdateSearchComponent);
    showSaveModal.componentInstance.savedSearch = this.savedSearch;
    //Keep any job association - eg when saving a default search for a job as a named search.
    showSaveModal.componentInstance.jobId = this.savedSearch?.sfJobOpp?.id;

    //Load search parameters.
    // Convert ids as we do for searches
    const request = this.searchForm.value;
    showSaveModal.componentInstance.searchCandidateRequest =
      this.getIdsMultiSelect(request);

    showSaveModal.result
      .then((savedSearch) => {
        if (create) {
          //If it is a create we want to navigate away from the default
          //saved search to the newly created search.
          const urlCommands = getCandidateSourceNavigation(savedSearch);
          this.router.navigate(urlCommands);
        }
        // After updating we want to reset the form so it's no longer dirty, this will allow users to bypass the
        // unsaved changes guard.
        this.populateFormWithSavedSearch(this.searchForm.value);
        this.searchForm.markAsPristine();
      })
      .catch(() => {
      });
  }

  deleteSavedSearchModal() {
    const deleteSavedSearchModal = this.modalService.open(ConfirmationComponent, {
      centered: true,
      backdrop: 'static'
    });

    deleteSavedSearchModal.componentInstance.message =
      'Are you sure you want to delete "' + this.savedSearch.name + '"';

    deleteSavedSearchModal.result
      .then((result) => {
        if (result === true) {
          this.savedSearchService.delete(this.savedSearch.id).subscribe(
            () => {
              this.router.navigate(['search']);
              this.loading = false;
            },
            (error) => {
              this.error = error;
              this.loading = false;
            });
        }
      })
      .catch(() => { });
  }

  populateFormWithSavedSearch(request: SearchCandidateRequest) {
    /* Do a blanket patch of all form fields */
    Object.keys(this.searchForm.controls).forEach(name => {
      this.searchForm.controls[name].patchValue(request[name]);
    });

    let searchType = request.countrySearchType;
    if (searchType == null) {
      searchType = 'or';
    }
    this.searchForm.controls['countrySearchType'].patchValue(searchType);

    searchType = request.nationalitySearchType;
    if (searchType == null) {
      searchType = 'or';
    }
    this.searchForm.controls['nationalitySearchType'].patchValue(searchType);

    searchType = request.listAllSearchType;
    if (searchType == null) {
      searchType = 'and';
    }
    this.searchForm.controls['listAllSearchType'].patchValue(searchType);

    searchType = request.listAnySearchType;
    if (searchType == null) {
      searchType = 'or';
    }
    this.searchForm.controls['listAnySearchType'].patchValue(searchType);


    // For the multiselects we have to set the corresponding id/name object by searching for the
    // values in the given search request in the complete set of drop down options for that field.

    /* STATUSES */

    let statuses: EnumOption[] = [];
    if (request.statuses) {
      statuses = enumKeysToEnumOptions(request.statuses, CandidateStatus);
    }
    this.searchForm.controls['statusesDisplay'].patchValue(statuses);

    /* ENGLISH PROFICIENCY */
    this.englishLanguagePicker.patchModel({
      writtenLevel: request.englishMinWrittenLevel,
      spokenLevel: request.englishMinSpokenLevel
    });

    /* OCCUPATIONS */
    let occupations = [];
    if (request.occupationIds && this.candidateOccupations) {
      occupations = this.candidateOccupations
        .filter(c => request.occupationIds.indexOf(c.id) !== -1);
    }
    this.searchForm.controls['occupations'].patchValue(occupations);

    /* MODIFIED DATES */
    if (request.lastModifiedFrom) {
      const [y, m, d] = request.lastModifiedFrom.split('-');
      const date: NgbDate = new NgbDate(Number(y), Number(m), Number(d));
      this.modifiedDatePicker.selectDate(date);
    }
    if (request.lastModifiedTo) {
      const [y, m, d] = request.lastModifiedTo.split('-');
      const date: NgbDate = new NgbDate(Number(y), Number(m), Number(d));
      this.modifiedDatePicker.selectDate(date);
    }

    /* PARTNERS */
    let partners = [];
    if (request.partnerIds && this.partners) {
      partners = this.partners.filter(c => request.partnerIds.indexOf(c.id) !== -1);
    }
    this.searchForm.controls['partners'].patchValue(partners);

    /* EDUCATION MAJORS */
    let educationMajors = [];
    if (request.educationMajorIds && this.educationMajors) {
      educationMajors = this.educationMajors
        .filter(c => request.educationMajorIds.indexOf(c.id) !== -1);
    }
    this.searchForm.controls['educationMajors'].patchValue(educationMajors);

    /* SURVEY TYPES */
    let surveyTypes = [];
    if (request.surveyTypeIds && this.surveyTypes) {
      surveyTypes = this.surveyTypes
        .filter(c => request.surveyTypeIds.indexOf(c.id) !== -1);
    }
    this.searchForm.controls['surveyTypes'].patchValue(surveyTypes);

    /* OTHER LANGUAGE */
    this.otherLanguagePicker.patchModel({
      languageId: request.otherLanguageId,
      writtenLevel: request.otherMinWrittenLevel,
      spokenLevel: request.otherMinSpokenLevel
    });

    /* COUNTRIES */
    let countries = [];
    if (request.countryIds && this.countries) {
      countries = this.countries.filter(c => request.countryIds.indexOf(c.id) !== -1);
    }
    this.searchForm.controls['countries'].patchValue(countries);

    /* NATIONALITIES */
    let nationalities = [];
    if (request.nationalityIds && this.nationalities) {
      nationalities = this.nationalities.filter(c => request.nationalityIds.indexOf(c.id) !== -1);
    }
    this.searchForm.controls['nationalities'].patchValue(nationalities);

    /* UNHCR STATUSES */
    let unhcrStatuses: EnumOption[] = [];
    if (request.unhcrStatuses) {
      unhcrStatuses = enumKeysToEnumOptions(request.unhcrStatuses, UnhcrStatus);
    }
    this.searchForm.controls['unhcrStatusesDisplay'].patchValue(unhcrStatuses);

    /* JOINED SEARCHES */
    while (this.searchJoinArray.length) {
      this.searchJoinArray.removeAt(0); // Clear the form array
    }
    if (request['searchJoinRequests'].length) {
      request['searchJoinRequests'].forEach((join) => {
        this.searchJoinArray.push(this.fb.group(join)); // If present, repopulate the array from the request
      });
      this.savedSearchService.get(request['searchJoinRequests'][0].savedSearchId).subscribe(result => {
        this.selectedBaseJoin = result;
      })
    }

    /* Perform a mouse event to force the multi-select components to update */
    this.formWrapper.nativeElement.click();

    /* Mark as pristine after search loaded, some fields may have been marked as dirty when
    populating the form fields but as this is the initial load of the form we don't want it to be dirty.
     */
    this.searchForm.markAsPristine();
    let isDirty = false;
    this.onFormChange.emit(isDirty);
  }

  get exclusionListId(): number {
    return this.exclusionListIdControl?.value;
  }

  get exclusionListIdControl(): AbstractControl {
    return this.searchForm.get('exclusionListId');
  }

  get listAllIds(): number[] {
    return this.listAllIdsControl?.value;
  }

  get listAllIdsControl(): AbstractControl {
    return this.searchForm.get('listAllIds');
  }

  get listAnyIds(): number[] {
    return this.listAnyIdsControl?.value;
  }

  get listAnyIdsControl(): AbstractControl {
    return this.searchForm.get('listAnyIds');
  }

  get searchJoinArray() {
    return this.searchForm.get('searchJoinRequests') as UntypedFormArray;
  }

  addSavedSearchJoin() {
    const joinSavedSearchComponent = this.modalService.open(JoinSavedSearchComponent, {
      centered: true,
      backdrop: 'static'
    });

    if (this.savedSearch != null){
      joinSavedSearchComponent.componentInstance.currentSavedSearchId = this.savedSearch.id;
    }

    joinSavedSearchComponent.result
      .then((join) => {
        this.searchJoinArray.push(this.fb.group(join));
      })
      .catch(() => { /* Isn't possible */
      });
  }

  deleteSavedSearchJoin(joinToRemove) {
    this.searchJoinArray.removeAt(this.searchJoinArray.value.findIndex(join => join.name === joinToRemove.name && join.searchType === joinToRemove.searchType))
  }

  handleDateSelected(e: { fromDate: NgbDateStruct, toDate: NgbDateStruct }, control: string) {
    this.searchForm.markAsDirty();
    if (e.fromDate) {
      this.searchForm.controls[control + 'From'].patchValue(e.fromDate.year + '-' + ('0' + e.fromDate.month).slice(-2) + '-' + ('0' + e.fromDate.day).slice(-2));
    } else {
      this.searchForm.controls[control + 'From'].patchValue(null);
    }
    if (e.toDate) {
      this.searchForm.controls[control + 'To'].patchValue(e.toDate.year + '-' + ('0' + e.toDate.month).slice(-2) + '-' + ('0' + e.toDate.day).slice(-2));
    } else {
      this.searchForm.controls[control + 'To'].patchValue(null);
    }
  }

  handleLanguageLevelChange(model: LanguageLevelFormControlModel, languageKey: string) {
    this.searchForm.markAsDirty();
    if (languageKey === 'english') {
      this.searchForm.controls['englishMinWrittenLevel'].patchValue(model.writtenLevel);
      this.searchForm.controls['englishMinSpokenLevel'].patchValue(model.spokenLevel);
    } else {
      // Update other language form values
      this.searchForm.controls['otherLanguageId'].patchValue(model.languageId);
      this.searchForm.controls['otherMinWrittenLevel'].patchValue(model.writtenLevel);
      this.searchForm.controls['otherMinSpokenLevel'].patchValue(model.spokenLevel);
    }
  }

  handleSearchTypeChange(control: string, value: 'and' | 'or' | 'not') {
    this.searchForm.controls[control].patchValue(value);
  }

  addBaseSearchJoin(baseSearch: SavedSearch) {
    this.selectedBaseJoin = {
      savedSearchId: baseSearch.id,
      name: baseSearch.name,
      searchType: "and"
    };
    // Clear the array before adding new base search
    if (this.searchJoinArray.length) {
      this.searchJoinArray.removeAt(0);
    }
    this.searchJoinArray.push(this.fb.group(this.selectedBaseJoin));
    this.searchForm.controls['searchJoinRequests'].markAsDirty();
    this.onFormChange.emit(this.searchForm.dirty);
  }

  deleteBaseSearchJoin() {
    if (this.searchJoinArray.length) {
      this.searchJoinArray.removeAt(0);
    }
    this.storedBaseJoin = null;
    this.selectedBaseJoin = null;
    this.searchForm.controls['searchJoinRequests'].markAsDirty();
    this.onFormChange.emit(this.searchForm.dirty);
  }

  extractSkills(): void {
    //Nothing to extract from - and no point showing skills left over from a previous,
    //since-cleared description.
    if (!this.hasRequirements()) {
      this.setExtractedSkills([]);
      return;
    }

    const request: ExtractSkillsRequest = {
      lang: "en",
      text: this.requirements
    }
    this.skillsService.extractSkills(request).subscribe({
      next: value => this.setExtractedSkills(value)
    })
  }

  canChangeSearchRequest(): boolean {
    return this.authorizationService.canEditCandidateSource(this.savedSearch);
  }

  public onSelectAll(options: any, formControl: any) {
    this.searchForm.controls[formControl].markAsDirty();
    this.searchForm.controls[formControl].patchValue(options);
  }

  public onClearAll(formControl: string) {
    this.searchForm.controls[formControl].markAsDirty();
    this.searchForm.controls[formControl].patchValue(null);
  }

  public getTooltip(formControlName: string) {
    let tooltip: string = '';
    const control = this.searchForm.controls[formControlName];
    const item = control.value[0];

    //If enum get the string value from each item, else get name.
    if (isEnumOption(item)) {
      control.value.forEach(i => tooltip += i.stringValue + ', ');
    } else {
      control.value.forEach(i => tooltip += i.name + ', ');
    }
    return tooltip.slice(0, -2);
  }

  public getPartnerDefaultMessage(): string {

    let s: string;

    //Source partners default to seeing only their candidates (unless they are the default partner)
    if (this.authorizationService.isSourcePartner() && !this.authorizationService.isDefaultPartner()) {
      s = "If nothing is specified here, the default is just to show candidates belonging to your partner.";
    } else {
      s = "If nothing is specified here, the default is to show candidates managed by any partner.";
    }

    return s;
  }

  public canViewCandidateName() {
    return this.authorizationService.canViewCandidateName();
  }

  public isEmployerPartner() {
    return this.authorizationService.isEmployerPartner();
  }

  public isEmptySearchTerms(): boolean {
    return !(this.currentSearchTerms && this.currentSearchTerms.length > 0);
  }

  /** Hides/shows the search request and scrolls to top */
  toggleSearchRequest() {
    this.showSearchRequest = !this.showSearchRequest;
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  public readonly CandidateSourceType = CandidateSourceType;
}
